import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "option"];
import UIFormControl from 'UIComponents/form/UIFormControl';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UILink from 'UIComponents/link/UILink';
import UISelect from 'UIComponents/input/UISelect';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import associationSupportsCustomLabels from 'automation-ui-associations/utils/associationSupportsCustomLabels';
import { associationLabelSettingsUrl } from 'automation-ui-references/routing/hubspotUrls';
import SyntheticEvent from 'UIComponents/core/SyntheticEvent';
import { useEffect } from 'react';
import useAssociationLabelOptions from 'automation-ui-associations/hooks/useAssociationLabelOptions';
import I18n from 'I18n';
import UIFlex from 'UIComponents/layout/UIFlex';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const AssociationOptionComponent = _ref => {
  let {
      children,
      option
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  return /*#__PURE__*/_jsx("span", Object.assign({}, rest, {
    children: /*#__PURE__*/_jsx(UITooltip, {
      title: option.disabledReason,
      disabled: !option.disabledReason,
      children: children
    })
  }));
};
const FlexibleAssociationSelect = ({
  readOnly,
  required = false,
  value,
  multi = false,
  onChange,
  label,
  extraSelectProps = {},
  fromObjectTypeId,
  fromCardinality,
  toObjectTypeId,
  help,
  dataTestId,
  hasReachedAssociationLimit,
  supportsNonFlexibleAssociations,
  shouldUseAlternateAllAssociationsLabel,
  shouldHideNoLabelSelect = true,
  inlineLabel = false
}) => {
  const [options, optionsLoading] = useAssociationLabelOptions({
    fromObjectTypeId,
    fromCardinality,
    toObjectTypeIds: [toObjectTypeId],
    hasReachedAssociationLimit,
    value,
    supportsNonFlexibleAssociations,
    shouldUseAlternateAllAssociationsLabel
  });
  useEffect(() => {
    const typedOnChange = onChange;
    const defaultAssociation = options.find(option => {
      var _option$associationDe;
      return (_option$associationDe = option.associationDefinition) === null || _option$associationDe === void 0 ? void 0 : _option$associationDe.isDefault;
    });
    if (options.length === 1 && !value) {
      typedOnChange(SyntheticEvent(options[0]));
    } else if (defaultAssociation && !value) {
      typedOnChange(SyntheticEvent(defaultAssociation));
    }
  }, [value, onChange, options]);
  const hideSelect = shouldHideNoLabelSelect && options.length === 1 && options[0].text === I18n.text('automation-ui-associations.flexible-associations.alternate-default-label_cob');
  if (hideSelect) {
    return null;
  }
  if (optionsLoading) {
    return /*#__PURE__*/_jsx(UILoadingSpinner, {
      grow: true,
      "data-test-id": "flexible-association-select-loading"
    });
  }
  const content = /*#__PURE__*/_jsx(UISelect, Object.assign({}, extraSelectProps, {
    value: value,
    options: options,
    readOnly: readOnly,
    onSelectedOptionChange: onChange,
    multi: multi,
    searchable: true,
    "data-test-id": dataTestId,
    itemComponent: AssociationOptionComponent
  }));
  if (!label) {
    return content;
  }
  if (inlineLabel) {
    return /*#__PURE__*/_jsxs(UIFlex, {
      children: [label, content]
    });
  }
  let tooltip;
  if (associationSupportsCustomLabels(fromObjectTypeId, toObjectTypeId)) {
    tooltip = /*#__PURE__*/_jsx(FormattedJSXMessage, {
      message: "automation-ui-action-features.flexible-associations.tooltips.create-labels_jsx",
      options: {
        link: associationLabelSettingsUrl(toObjectTypeId)
      },
      elements: {
        UILink
      }
    });
  } else {
    tooltip = /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation-ui-action-features.flexible-associations.tooltips.unsupported-object-type"
    });
  }
  return /*#__PURE__*/_jsx(UIFormControl, {
    "data-test-id": "flexible-association-field",
    label: label,
    help: help,
    required: required,
    tooltip: tooltip,
    children: content
  });
};
export default FlexibleAssociationSelect;